import React, { useState, useEffect, useRef } from 'react';
import '../styles/myRequests.css';
import { IconButton, TooltipHost } from '@fluentui/react';
import { initializeIcons } from '@fluentui/react/lib/Icons';
import AdvancedSearch from '../components/advancedSearch';
import clearicon from '../assets/closecircle.svg';
import emptybackground from '../assets/nofile.png';
import useAffidavitService from '../services/affidavitServices';
import { Refresh } from "@mui/icons-material";
import { Pagination } from '../components/Pagination';

initializeIcons();

const ITEMS_PER_PAGE = 20;

const SignedRequestsList = ({ courts, courtDivisions, loading, setLoading }) => {
    const searchInputRef = useRef(null);
    const [allAffidavitRequests, setAllAffidavitRequests] = useState([]);
    const [filteredAffidavitRequests, setFilteredAffidavitRequests] = useState([]);
    const [courtDivisionsForCourt, setCourtDivisionsForCourt] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [selectedCourtId, setSelectedCourtId] = useState("");
    const [selectedCourtDivisionId, setSelectedCourtDivisionId] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [tableHeight, setTableHeight] = useState('auto');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentDocumentUrl, setCurrentDocumentUrl] = useState('');
    const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false);
    const [message, setMessage] = useState("");
    const [searchCriteria, setSearchCriteria] = useState(null);

    const { fetchMyAffidavitData, fetchAffidavitByFilter  } = useAffidavitService();

    const defaultMessage = (
        <>
            No signed requests found. Would you like to create a new request? Click{' '}
            <a href='/apply-affidavit'>here</a> to apply.
        </>
    );

    const searchBoxStyles = {
        container: {
            display: "flex",
            alignItems: "center",
            border: "1px solid #0A722F",
            borderRadius: "10px",
            overflow: "hidden",
            marginTop: "auto"
        },
        input: {
            flex: 1,
            border: "none",
            outline: "none",
            padding: "10px 15px",
            fontSize: "14px",
            color: "#333",
            "::placeholder": { color: "#B0B0B0" },
        },
        button: {
            backgroundColor: "#0A722F",
            height: "100%",
            color: "#fff",
            padding: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "15%",
            cursor: "pointer",
        },
    };
// ─── Resize handler ───────────────────────────────────────────────────────
    useEffect(() => {
        const handleResize = () => setTableHeight('auto');
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
 
    // ─── Initial, page-change, and search fetch ──────────────────────────────
    useEffect(() => {
        loadData(currentPage, searchCriteria);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, searchCriteria]);
 
    const loadData = async (page = 1, criteria = null) => {
        try {
            setLoading(true);
            setMessage("");

            let response;
            if (criteria) {
                // Search mode. AdvancedSearch already forces status=Approved for this page.
                response = await fetchAffidavitByFilter(criteria, page, ITEMS_PER_PAGE);
            } else {
                response = await fetchMyAffidavitData(page, ITEMS_PER_PAGE, 'Approved');
            }

            if (response?.data?.length > 0) {
                setAllAffidavitRequests(response.data);
                setFilteredAffidavitRequests(response.data);
                setTotalCount(response.totalCount);
            } else {
                setAllAffidavitRequests([]);
                setFilteredAffidavitRequests([]);
                setTotalCount(0);
                setMessage(criteria
                    ? "Your search did not return any results. Please check your search criteria and try again."
                    : defaultMessage);
            }
        } catch (error) {
            console.error("Failed to fetch data:", error);
            setMessage("An error occurred while processing your data. Please try again later.");
        } finally {
            setLoading(false);
        }
    };
 
    // ─── Refresh ──────────────────────────────────────────────────────────────
    const handleRefresh = async () => {
        setSearchText("");
        setSelectedCourtId("");
        setSelectedCourtDivisionId("");
        setCourtDivisionsForCourt([]);
        if (searchInputRef.current) searchInputRef.current.value = "";
 
        const alreadyBrowsingPage1 = currentPage === 1 && searchCriteria === null;
        setSearchCriteria(null);
        setCurrentPage(1);
        if (alreadyBrowsingPage1) {
            await loadData(1, null);
        }
    };
 
    // ─── Modal ────────────────────────────────────────────────────────────────
    const handleDownload = (documentUrl) => {
        const viewerUrl = `/pdfjs/viewer.html?file=${encodeURIComponent(documentUrl)}`;
        setCurrentDocumentUrl(viewerUrl);
        setIsModalOpen(true);
    };
 
    const closeModal = () => {
        setIsModalOpen(false);
        setCurrentDocumentUrl('');
    };
 
    // ─── Client-side filtering triggers server-side filtering by setting criteria state ───────────────
    const applyServerFilter = (courtId, courtDivisionId, searchValue) => {
        const criteria = {};

        if (courtId && parseInt(courtId) !== 0) {
            const court = courts.find((c) => c.id === parseInt(courtId));
            if (court) criteria.courtName = court.courtName;
        }

        if (courtDivisionId && parseInt(courtDivisionId) !== 0) {
            const division = courtDivisions.find((d) => d.id === parseInt(courtDivisionId));
            if (division) criteria.courtDivision = division.courtDivisionName;
        }

        if (searchValue) criteria.documentId = searchValue;

        criteria.status = "Approved";

        const hasCriteria = Object.keys(criteria).length > 0;

        setSearchCriteria(hasCriteria ? criteria : null);
        setCurrentPage(1);
    };

    const handleSearchTextChange = () => {
        const searchValue = searchInputRef.current.value;
        setSearchText(searchValue);
        applyServerFilter(selectedCourtId, selectedCourtDivisionId, searchValue);
    };

    const handleCourtDropdownChange = (event) => {
        const courtId = event.target.value;
        setSelectedCourtDivisionId("");
        setSelectedCourtId(courtId);

        if (courtId === "0") {
            setCourtDivisionsForCourt([{ id: 0, courtDivisionName: "All" }]);
        } else {
            const divisionsForCourt = courtDivisions.filter(
                (division) => division.courtId === parseInt(courtId)
            );
            divisionsForCourt.unshift({ id: 0, courtDivisionName: "All" });
            setCourtDivisionsForCourt(divisionsForCourt);
        }

        applyServerFilter(courtId, "", searchText);
    };

    const handleCourtDivisionDropdownChange = (event) => {
        const divisionId = event.target.value;
        setSelectedCourtDivisionId(divisionId);
        applyServerFilter(selectedCourtId, divisionId, searchText);
    };

    const handleClearButtonClick = () => {
        setSearchText("");
        setSelectedCourtId("");
        setSelectedCourtDivisionId("");
        setCourtDivisionsForCourt([]);
        setMessage("");
        if (searchInputRef.current) searchInputRef.current.value = "";

        const alreadyBrowsingPage1 = currentPage === 1 && searchCriteria === null;
        setSearchCriteria(null);
        setCurrentPage(1);
        if (alreadyBrowsingPage1) {
            loadData(1, null);
        }
    };
 
    const handleOpenAdvancedSearch = () => {
        setIsAdvancedSearchOpen((prev) => !prev);
    };
 
    // AdvancedSearch hands back CRITERIA (with status forced to Approved for this page).
    const handleAffidavitRequestsSearchResults = (criteria) => {
        setSearchCriteria(criteria);
        setCurrentPage(1);
    };
 
    // ─── Pagination ───────────────────────────────────────────────────────────
    const currentItems = filteredAffidavitRequests;
    const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);
 
    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <div className='container-content'>
            <div className="page-title">
                <p>Signed Request List</p>
            </div>

            <div className="search-and-filter-container">
                <div className="filter-container">
                    <div className="search-box-container" style={searchBoxStyles.container}>
                        <input
                            type="text"
                            placeholder="Search by Document Id"
                            ref={searchInputRef}
                            className="search-input"
                            style={searchBoxStyles.input}
                            onKeyDown={(e) => e.key === 'Enter' && handleSearchTextChange()}
                        />
                        <IconButton
                            iconProps={{ iconName: "Search" }}
                            style={searchBoxStyles.button}
                            onClick={handleSearchTextChange}
                        />
                    </div>

                    <div className="select-group">
                        <div className="select-item">
                            <label htmlFor="court-select">Filter by court name</label>
                            <select
                                id="court-select"
                                className="select-left"
                                value={selectedCourtId}
                                onChange={handleCourtDropdownChange}
                            >
                                <option value="" disabled hidden>All Courts</option>
                                {courts.length > 0 && courts.map((court) => (
                                    <option value={court.id} key={court.id}>{court.courtName}</option>
                                ))}
                            </select>
                        </div>
                        <div className="select-item">
                            <label htmlFor="division-select" disabled={!selectedCourtId}>
                                Filter by division
                            </label>
                            <select
                                id="division-select"
                                value={selectedCourtDivisionId}
                                onChange={handleCourtDivisionDropdownChange}
                                className="select-right"
                                disabled={!selectedCourtId}
                            >
                                <option value="" disabled hidden>All Divisions</option>
                                {courtDivisionsForCourt.map((division) => (
                                    <option value={division.id} key={division.id}>
                                        {division.courtDivisionName}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="button-group">
                        <button className="clear-button" onClick={handleClearButtonClick}>
                            <img src={clearicon} style={{ width: '15px' }} alt="Clear filters" />
                            Reset
                        </button>
                        <button className="clear-button" onClick={handleRefresh}>
                            <Refresh style={{ width: '15px' }} />
                            Refresh
                        </button>
                        <button className="mrl-btn mrl-btn-advanced" onClick={handleOpenAdvancedSearch}>
                            Advanced Search
                        </button>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="loader"></div>
            ) : filteredAffidavitRequests.length === 0 ? (
                <div className="emptytext">
                    <img src={emptybackground} alt="No records found" />
                    <p style={{ fontStyle: 'italic' }}>
                        {message || "An error occurred while processing your data. Please try again later."}
                    </p>
                </div>
            ) : (
                <div className='table-wrapper' style={{ height: tableHeight }}>
                    <table className="requests-table">
                        <thead>
                            <tr>
                                <th>Document ID</th>
                                <th>Date</th>
                                <th>Court Name</th>
                                <th>Court Division</th>
                                <th>Current Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentItems.map((row, index) => (
                                <tr key={row.id ?? index} className="request-row">
                                    <td>{row.documentId}</td>
                                    <td>
                                        {new Date(row.createdAt)
                                            .toLocaleDateString('en-GB', {
                                                day: '2-digit',
                                                month: 'short',
                                                year: 'numeric'
                                            })
                                            .replace(/ /g, '-')}
                                    </td>
                                    <td>{row.court?.courtName}</td>
                                    <td>{row.courtDivision?.courtDivisionName}</td>
                                    <td>
                                        <span
                                            style={{
                                                backgroundColor:
                                                    row.status === 'Approved' ? '#2BA745' :
                                                    row.status === 'Rejected' ? '#721c24' : '#FFA500',
                                                color: 'white',
                                                borderRadius: '4px',
                                                padding: '5px 10px',
                                                textAlign: 'center',
                                                width: '100px',
                                                display: 'inline-block',
                                            }}
                                        >
                                            {row.status}
                                        </span>
                                    </td>
                                    <td>
                                        <TooltipHost content="View Document">
                                            <IconButton
                                                iconProps={{ iconName: 'View' }}
                                                onClick={() => handleDownload(row.signedDocumentUrl)}
                                                styles={{
                                                    root: { backgroundColor: '#00000000', color: 'black', width: '20px', height: '20px' },
                                                    menuIcon: { fontSize: '18', marginLeft: '12px' }
                                                }}
                                            />
                                        </TooltipHost>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Modal for Document Preview */}
            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <button className="close-button" onClick={closeModal}>
                            <img src={clearicon} alt="Close" />
                            Close
                        </button>
                        <iframe
                            src={currentDocumentUrl}
                            style={{
                                width: '100%', height: '100%', marginTop: '10px',
                                flex: 1, boxSizing: 'border-box', overflow: 'auto',
                                WebkitOverflowScrolling: 'touch'
                            }}
                            title="Document Preview"
                        />
                    </div>
                </div>
            )}

            {/* Pagination Controls */}
            {totalPages > 1 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalCount={totalCount}
                    itemsPerPage={ITEMS_PER_PAGE}
                    onPageChange={setCurrentPage}
                />
            )}

            {isAdvancedSearchOpen && (
                <AdvancedSearch
                    type={"signed"}
                    handleOpen={handleOpenAdvancedSearch}
                    courts={courts}
                    courtDivisions={courtDivisions}
                    returnSearchResults={handleAffidavitRequestsSearchResults}
                />
            )}
        </div>
    );
};

export default SignedRequestsList;


// import React, { useState, useEffect, useRef } from 'react';
// import '../styles/myRequests.css';
// import { Icon, IconButton, TooltipHost } from '@fluentui/react';
// import { initializeIcons } from '@fluentui/react/lib/Icons';
// import AdvancedSearch from '../components/advancedSearch';
// import clearicon from '../assets/closecircle.svg'; 
// import emptybackground from '../assets/nofile.png';
// import useAffidavitService from '../services/affidavitServices';
// import {
//     Refresh
// } from "@mui/icons-material";

// initializeIcons();

// const SignedRequestsList = ({affidavitRequests, courts, courtDivisions, loading,setLoading}) => {
//     const searchInputRef = useRef(null); 
//     const [allAffidavitRequests, setAllAffidavitRequests] = useState([]);
//     const [filteredAffidavitRequests, setFilteredAffidavitRequests] = useState([]);
//     const [courtDivisionsForCourt, setCourtDivisionsForCourt] = useState([]);
//     const [searchText, setSearchText] = useState("");
//     const [selectedCourtId, setSelectedCourtId] = useState("");
//     const [selectedCourtDivisionId, setSelectedCourtDivisionId] = useState("");
//     const [currentPage, setCurrentPage] = useState(1);
//     const [itemsPerPage, setItemsPerPage] = useState(15);
//     const [tableHeight, setTableHeight] = useState('auto');
//     const [isModalOpen, setIsModalOpen] = useState(false);
//     const [currentDocumentUrl, setCurrentDocumentUrl] = useState('');
//     const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false);
//     const [message, setMessage] = useState("");
//     const {fetchMyAffidavitData} = useAffidavitService();
    
    
//     const searchBoxStyles = {
//         container: {
//           display: "flex",
//           alignItems: "center",
//           border: "1px solid #0A722F",
//           borderRadius: "10px",
//           overflow: "hidden",
//         //   width: "250px",
//           marginTop: "auto"
//         },
//         input: {
//           flex: 1,
//           border: "none",
//           outline: "none",
//           padding: "10px 15px",
//           fontSize: "14px",
//           color: "#333",
//           "::placeholder": {
//             color: "#B0B0B0",
//           },
//         },
//         button: {
//           backgroundColor: "#0A722F",
//           height: "100%",
//           color: "#fff",
//           padding: "10px",
//           display: "flex",
//           alignItems: "center",
//           justifyContent: "center",
//           width: "15%",
//           cursor: "pointer",
//         },
//       };
//     const handleDownload = (documentUrl) => {
//         const viewerUrl = `/pdfjs/viewer.html?file=${encodeURIComponent(documentUrl)}`;
//         setCurrentDocumentUrl(viewerUrl);
//         setIsModalOpen(true);
//     };

//     const closeModal = () => {
//         setIsModalOpen(false);
//         setCurrentDocumentUrl('');
//     };


//     useEffect(() => {
//         const handleResize = () => {
//             const width = window.innerWidth;

//             if (width >= 1200) { // Large screens
//                 setItemsPerPage(20);
//                 setTableHeight('auto');
//             } else if (width >= 768) { // Medium screens
//                 setItemsPerPage(10);
//                 setTableHeight('auto');
//             } else { // Small screens
//                 setItemsPerPage(5);
//                 setTableHeight('auto');
//             }
//         };

//         // Set initial value on mount
//         handleResize();
//         window.addEventListener('resize', handleResize);

//         return () => {
//             window.removeEventListener('resize', handleResize);
//         };
//     }, []);

//     useEffect(() => {
//         const fetchData = async () => {
//             const defaultMessage = (
//                 <>
//                   No signed requests found. Would you like to create a new request? Click <a href='/apply-affidavit'>here</a> to apply.
//                 </>
//               );

//             try {

//                         if (affidavitRequests && affidavitRequests.length > 0) {
//                             processAffidavitRequests(affidavitRequests,defaultMessage);
//                         } else {
//                             setMessage(defaultMessage);
//                         }
//                 }
//             catch (error) {
//                 console.error("Failed to fetch data req:", error);
//                 setMessage("An error occurred while processing your data. Please try again later");
//             } 
//         };

//         fetchData();
//     }, [affidavitRequests,courts,courtDivisions]);

//     const processAffidavitRequests = (requests, defaultMessage) => {

//        const affidavitResponse = requests?.filter((request) => request.status =='Approved');
//        const latestAffidavit = affidavitResponse?.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
                       
//         setAllAffidavitRequests(latestAffidavit);
//         setFilteredAffidavitRequests(latestAffidavit);

//         if (latestAffidavit.length === 0) {
//             setMessage(defaultMessage);
//         }
//     };
//     const handleRefresh = async() => {
//         try
//             {
//                 const defaultMessage = (
//                     <>
//                       No pending requests found. Would you like to create a new request? Click <a href='/apply-affidavit'>here</a> to apply.
//                     </>
//                   );
//                 setLoading(true);
//                 const fetchedaffidavitrecord = await fetchMyAffidavitData();
//                 if (fetchedaffidavitrecord && fetchedaffidavitrecord.length > 0) {
//                     processAffidavitRequests(fetchedaffidavitrecord,defaultMessage);
//                 } else {
//                     setMessage(defaultMessage);
//                 }
//             }
//             catch(error)
//             {
//             console.error("Failed to refresh data:", error);
//             throw error;
//             }finally
//             {
//                 setLoading(false);
//             }
//             }
//     const handleFiltering = (courtId, courtDivisionId, searchValue) => {
//         let filteredRequests = allAffidavitRequests;

//         if (courtId) {
//             courtId = parseInt(courtId);
//             if (courtId !== 0) {
//                 filteredRequests = filteredRequests.filter((affidavit) => affidavit.courtId === courtId);
//             }
//         }

//         if (courtDivisionId) {
//             courtDivisionId = parseInt(courtDivisionId);
//             if (courtDivisionId !== 0) {
//                 filteredRequests = filteredRequests.filter((affidavit) => affidavit.courtDivisionId === courtDivisionId);
//             }
//         }

//         if (searchValue) {
//             filteredRequests = filteredRequests.filter((affidavit) => affidavit.documentId.includes(searchValue));
//         }

//         setFilteredAffidavitRequests(filteredRequests);
//         if(filteredRequests.length === 0){
//             setMessage("Your search did not return any results. Please check your search criteria and try again");
//         }
//         setCurrentPage(1);
//     }

//     const handleSearchTextChange = () => {
//         const searchValue = searchInputRef.current.value;
//         setSearchText(searchValue);
//         handleFiltering(selectedCourtId, selectedCourtDivisionId, searchValue);
//     }

//     // Handle dropdown changes and filter
//     const handleCourtDropdownChange = (event) => {
//         const courtId = event.target.value;
//         setSelectedCourtDivisionId("");
//         setSelectedCourtId(courtId);

//         if (courtId === "0") {
//             setCourtDivisionsForCourt([{ id: 0, courtDivisionName: "All" }]);
//         } else {
//             let courtDivisionsForSelectedCourt = courtDivisions.filter((division) => division.courtId === parseInt(courtId));
//             courtDivisionsForSelectedCourt.unshift({ id: 0, courtDivisionName: "All" });
//             setCourtDivisionsForCourt(courtDivisionsForSelectedCourt);
//         }

//         handleFiltering(courtId, "", searchText);
//     }

//     const handleCourtDivisionDropdownChange = (event) => {
//         setSelectedCourtDivisionId(event.target.value);
//         handleFiltering(selectedCourtId, event.target.value, searchText);
//     }

//     const handleClearButtonClick = () => {
//         setSearchText("");
//         setSelectedCourtId("");
//         setSelectedCourtDivisionId("");
//         searchInputRef.current.value = ""; // Clear the input field
//         handleFiltering("", "", "");
//     }

//     const handleOpenAdvancedSearch = () => {
//         setIsAdvancedSearchOpen(!isAdvancedSearchOpen);
//     }

//     const handleAffidavitRequestsSearchResults = (requests) => {
//         setFilteredAffidavitRequests(requests)
//     }

//     // Pagination Logic
//     const indexOfLastItem = currentPage * itemsPerPage;
//     const indexOfFirstItem = indexOfLastItem - itemsPerPage;
//     const currentItems = filteredAffidavitRequests.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(indexOfFirstItem, indexOfLastItem);
//     const totalPages = Math.ceil(filteredAffidavitRequests.length / itemsPerPage);

//     return (
//         <div className='container-content'>
//             <div className="page-title">
//                 <p>Signed Request List</p>
//             </div>
//             <div className="search-and-filter-container">              
//                 <div className="filter-container">
//                 <div className="search-box-container" style={searchBoxStyles.container}>
//                                                 <input
//                                                     type="text"
//                                                     placeholder="Search by Document Id"
//                                                     defaultValue={searchText} // Use value instead of defaultValue
//                                                     ref={searchInputRef}
//                                                     className="search-input"
//                                                     style={searchBoxStyles.input}
//                                                 />
//                                                 <IconButton
//                                                     iconProps={{ iconName: "Search" }}
//                                                     style={searchBoxStyles.button}
//                                                     onClick={() => handleSearchTextChange()}
//                                                 />
//                         </div>
//                     {/* <button className="filter-button" onClick={handleFiltering}>Filter</button> */}
//                     <div className="select-group">
//                     <div className="select-item">
//                         <label htmlFor="court-select"> Filter by court name</label>
//                         <select
//                             id="court-select"
//                             className="select-left"
//                             value={selectedCourtId}
//                             onChange={handleCourtDropdownChange}
//                         >
//                             <option value="" disabled hidden>
//                                 All Courts
//                             </option>
//                             {courts.length > 0 && courts.map((court) => {
//                                 return <option value={court.id} key={court.id}>{court.courtName}</option>
//                             })}
//                         </select>
//                     </div>
//                     <div className="select-item">
//                         <label htmlFor="division-select"
//                                disabled={!selectedCourtId}>Filter by division</label>
//                         <select
//                             id="division-select"
//                             value={selectedCourtDivisionId}
//                             onChange={handleCourtDivisionDropdownChange}
//                             className="select-right"
//                             disabled={!selectedCourtId}
//                         >
//                             <option value="" disabled hidden>
//                                 All Divisions
//                             </option>
//                             {courtDivisionsForCourt.map((division) => {
//                                 return <option value={division.id} key={division.id}>{division.courtDivisionName}</option>
//                             })}
//                         </select>
//                     </div>
//                     </div>
//                             <div className="button-group">
//                                 <button className="clear-button" onClick={handleClearButtonClick}>
//                                     <img src={clearicon} style={{width:'15px'}}alt="Clear Icon" />
//                                     Reset
//                                 </button>
//                                 <button className="clear-button"  onClick={() => handleRefresh()}>
//                                     <Refresh style={{width:'15px'}}/>
//                                     Refresh
//                                  </button>
//                                 <button className="mrl-btn mrl-btn-advanced" onClick={handleOpenAdvancedSearch}>Advanced Search</button>                             
//                             </div>
//                 </div>
//             </div>

//              {loading ? (
//                     <div className="loader"></div>
//                 ) : filteredAffidavitRequests.length === 0 ? (
//                     <div className="emptytext">
//                         <img src={emptybackground} alt='no file image'/>
//                         <p style={{ fontStyle: 'italic' }}>
//                         {message ? message : "An error occurred while processing your data. Please try again later"}
//                         </p> </div>
//                 ) : ( 
//                 <div className='table-wrapper' style={{ Height: tableHeight }}>
//                     <table className="requests-table">
//                         <thead>
//                             <tr>
//                                 <th>Document ID</th>
//                                 <th>Date</th>
//                                 <th>Court Name</th>
//                                 <th>Court Division</th>
//                                 <th>Current Status</th>
//                                 <th>Actions</th>
//                             </tr>
//                         </thead>
//                         <tbody>
//                             {currentItems.map((row, index) => (
//                                 <tr key={index} className="request-row">
//                                     <td>{row.documentId}</td>
//                                     <td>{new Date(row.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-')}</td>
//                                     <td>{row.court?.courtName}</td>
//                                     <td>{row.courtDivision?.courtDivisionName}</td>
//                                     <td>
//                                     <span
//                                         style={{
//                                             backgroundColor: row.status === 'Approved' ? '#2BA745' : row.status === 'Rejected' ? '#721c24' : '#FFA500',
//                                             color: 'white',
//                                             borderRadius: '4px',
//                                             padding: '5px 10px',
//                                             textAlign: 'center',
//                                             width: '100px', // Set a fixed width
//                                             display: 'inline-block', 
//                                         }}
//                                         >
//                                     {row.status}
//                                     </span>
//                                     </td>            
//                                     <td>
//                                         <TooltipHost content= "View Document">
//                                         <IconButton
//                                             iconProps={{ iconName: 'View' }}
//                                             onClick={() => handleDownload(row.signedDocumentUrl)}
//                                             styles={{
//                                                 root: {
//                                                     backgroundColor: '#00000000',
//                                                     color: 'black',
//                                                     width: '20px',
//                                                     height: '20px'
//                                                 },
//                                                 menuIcon: {
//                                                     fontSize: '18',
//                                                     marginLeft: '12px'
//                                                 }
//                                             }}
//                                         />
//                                         </TooltipHost>
//                                     </td>
//                                 </tr>
//                             ))}
//                         </tbody>
//                     </table>
//                 </div>
//                 )}
           

//             {/* Modal for Document Preview */}
//             {isModalOpen && (
//                 <div className="modal-overlay">
//                     <div className="modal-content">
//                     <button className="close-button" onClick={closeModal}>
//                                     <img src={clearicon} alt="Close" />
//                                     Close
//                     </button>
//                         <iframe
//                             src={currentDocumentUrl}
//                             style={{ width: '100%', height: '100%', marginTop: '10px' ,height: '100%', flex: 1, boxSizing: 'border-box', overflow: 'auto',WebkitOverflowScrolling: 'touch' }}
//                             title="Document Preview"                            
//                         />
//                     </div>
//                 </div>
//             )}

//             {/* Pagination Controls */}
//             <div className="pagination" style={{ display: filteredAffidavitRequests && filteredAffidavitRequests.length > 0 ? '' : 'none' }}>
//                 <button onClick={() => setCurrentPage(page => Math.max(page - 1, 1))} disabled={currentPage === 1}>
//                     Previous
//                 </button>
//                 <span>Page {currentPage} of {totalPages}</span>
//                 <button onClick={() => setCurrentPage(page => Math.min(page + 1, totalPages))} disabled={currentPage === totalPages}>
//                     Next
//                 </button>
//             </div>

//             {isAdvancedSearchOpen &&
//                 <AdvancedSearch
//                     type={"signed"}
//                     handleOpen={handleOpenAdvancedSearch}
//                     courts={courts}
//                     courtDivisions={courtDivisions}
//                     returnSearchResults={handleAffidavitRequestsSearchResults}
//                 />
//             }
//         </div>
//     );
// };

// export default SignedRequestsList;